import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { TooltipProvider } from './components/ui/tooltip';
import { ThemeProvider } from './contexts/ThemeContext';
import { AppConfigProvider, useAppConfig } from './contexts/AppConfigContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UpgradeModalProvider } from './contexts/UpgradeModalContext';
import Header from './components/Header';
import BetaBanner from './components/BetaBanner';
import MaintenanceScreen from './components/MaintenanceScreen';
import PhaseAwarePopup from './components/PhaseAwarePopup';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Portal from './pages/Portal';
import Pricing from './pages/Pricing';
import BillingSuccess from './pages/BillingSuccess';
import Share from './pages/Share';
import { Loader2 } from 'lucide-react';
import './App.css';

function InitGate({ children }) {
    const { initializing } = useAuth();
    if (initializing) {
        return (
            <div
                data-testid="auth-init-gate"
                className="min-h-[calc(100vh-73px)] grid place-items-center bg-background text-muted-foreground"
            >
                <div className="flex items-center gap-3 text-xs uppercase tracking-[0.28em]">
                    <Loader2 className="h-4 w-4 animate-spin text-[#FF6B35]" />
                    Restoring session…
                </div>
            </div>
        );
    }
    return children;
}

function Protected({ children }) {
    const { user, initializing } = useAuth();
    if (initializing) {
        return (
            <div className="min-h-[calc(100vh-73px)] grid place-items-center text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin text-[#FF6B35]" />
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    return children;
}

function Shell() {
    const cfg = useAppConfig();
    const navigate = useNavigate();
    if (cfg.maintenance_mode) return <MaintenanceScreen />;
    return (
        <>
            <BetaBanner />
            <Header />
            <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/auth/callback" element={<Login />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/p/:slug" element={<Portal />} />
                <Route path="/s/:token" element={<Share />} />
                <Route path="/dashboard" element={<Protected><InitGate><Dashboard /></InitGate></Protected>} />
                <Route path="/billing/success" element={<Protected><InitGate><BillingSuccess /></InitGate></Protected>} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            {/* Bound to AuthContext resolution — renders on every login session,
                route-independent. Navigates to the requested dashboard tab. */}
            <PhaseAwarePopup onNavigate={(tab) => navigate(`/dashboard?tab=${tab}`)} />
        </>
    );
}

export default function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <ThemeProvider>
                    <AppConfigProvider>
                        <AuthProvider>
                            <UpgradeModalProvider>
                                <TooltipProvider delayDuration={200}>
                                    <Shell />
                                    <Toaster position="top-right" />
                                </TooltipProvider>
                            </UpgradeModalProvider>
                        </AuthProvider>
                    </AppConfigProvider>
                </ThemeProvider>
            </BrowserRouter>
        </div>
    );
}
